import { z } from 'zod';

import { MAX_WILDCARD_STARS, splitWildcard } from '../../filter/services/filter-dsl.compiler';

export enum EConditionKind {
  TEXT = 'text',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  LIST = 'list',
}

export enum EConditionOp {
  IS = 'is',
  IS_NOT = 'is_not',
  CONTAINS = 'contains',
  NOT_CONTAINS = 'not_contains',
  CONTAINS_ANY = 'contains_any',
  STARTS_WITH = 'starts_with',
  ENDS_WITH = 'ends_with',
  MATCHES = 'matches',
  IS_ONE_OF = 'is_one_of',
  IS_NOT_ONE_OF = 'is_not_one_of',
  GT = 'gt',
  GTE = 'gte',
  LT = 'lt',
  LTE = 'lte',
  BETWEEN = 'between',
  IS_TRUE = 'is_true',
  IS_FALSE = 'is_false',
  HAS_MORE_THAN = 'has_more_than',
  IS_EMPTY = 'is_empty',
  IS_NOT_EMPTY = 'is_not_empty',
  EXISTS = 'exists',
  NOT_EXISTS = 'not_exists',
}

const PRESENCE = [EConditionOp.EXISTS, EConditionOp.NOT_EXISTS] as const;

/** "Operators follow the field" — the editor offers exactly these per kind. */
export const CONDITION_OPERATORS: Readonly<Record<EConditionKind, readonly EConditionOp[]>> = {
  [EConditionKind.TEXT]: [
    EConditionOp.IS,
    EConditionOp.IS_NOT,
    EConditionOp.CONTAINS,
    EConditionOp.NOT_CONTAINS,
    EConditionOp.STARTS_WITH,
    EConditionOp.ENDS_WITH,
    EConditionOp.MATCHES,
    EConditionOp.IS_ONE_OF,
    EConditionOp.IS_NOT_ONE_OF,
    EConditionOp.IS_EMPTY,
    EConditionOp.IS_NOT_EMPTY,
    ...PRESENCE,
  ],
  [EConditionKind.NUMBER]: [
    EConditionOp.IS,
    EConditionOp.IS_NOT,
    EConditionOp.GT,
    EConditionOp.GTE,
    EConditionOp.LT,
    EConditionOp.LTE,
    EConditionOp.BETWEEN,
    EConditionOp.IS_ONE_OF,
    EConditionOp.IS_NOT_ONE_OF,
    ...PRESENCE,
  ],
  [EConditionKind.BOOLEAN]: [EConditionOp.IS_TRUE, EConditionOp.IS_FALSE, ...PRESENCE],
  [EConditionKind.LIST]: [
    EConditionOp.CONTAINS,
    EConditionOp.NOT_CONTAINS,
    EConditionOp.CONTAINS_ANY,
    EConditionOp.HAS_MORE_THAN,
    EConditionOp.IS_EMPTY,
    EConditionOp.IS_NOT_EMPTY,
    ...PRESENCE,
  ],
};

export const VALUELESS_OPS: readonly EConditionOp[] = [
  EConditionOp.IS_TRUE,
  EConditionOp.IS_FALSE,
  EConditionOp.IS_EMPTY,
  EConditionOp.IS_NOT_EMPTY,
  EConditionOp.EXISTS,
  EConditionOp.NOT_EXISTS,
];

export const LIST_VALUE_OPS: readonly EConditionOp[] = [
  EConditionOp.IS_ONE_OF,
  EConditionOp.IS_NOT_ONE_OF,
  EConditionOp.CONTAINS_ANY,
];

const WILDCARD_OPS: readonly EConditionOp[] = [
  EConditionOp.MATCHES,
  EConditionOp.IS_ONE_OF,
  EConditionOp.IS_NOT_ONE_OF,
];

const scalar = z.union([z.string().max(1_000), z.number(), z.boolean(), z.null()]);

export const FlowConditionSchema = z
  .object({
    path: z.string().trim().min(1).max(300),
    kind: z.enum(EConditionKind),
    op: z.enum(EConditionOp),
    value: z.union([scalar, z.array(scalar).max(100)]).optional(),
  })
  .superRefine((condition, ctx) => {
    if (!CONDITION_OPERATORS[condition.kind].includes(condition.op)) {
      ctx.addIssue({
        code: 'custom',
        path: ['op'],
        message: `"${condition.op}" does not apply to a ${condition.kind} field`,
      });
      return;
    }
    const issue = valueProblem(condition.kind, condition.op, condition.value);
    if (issue) ctx.addIssue({ code: 'custom', path: ['value'], message: issue });
  });

function valueProblem(kind: EConditionKind, op: EConditionOp, value: unknown): string | null {
  if (VALUELESS_OPS.includes(op)) return null;
  if (value === undefined) return 'a value is required';
  if (LIST_VALUE_OPS.includes(op)) {
    if (!Array.isArray(value) || value.length === 0) return 'pick at least one value';
  } else if (op === EConditionOp.BETWEEN) {
    if (!Array.isArray(value) || value.length !== 2) return 'between takes two values';
  } else if (Array.isArray(value)) {
    return 'one value, not a list';
  }
  if (op === EConditionOp.HAS_MORE_THAN) {
    return typeof value === 'number' && Number.isInteger(value) && value >= 0
      ? null
      : 'a whole number';
  }
  const values: unknown[] = Array.isArray(value) ? value : [value];
  if (kind === EConditionKind.NUMBER && values.some((v) => typeof v !== 'number')) {
    return values.length > 1 ? 'numbers only' : 'a number';
  }
  if (kind === EConditionKind.TEXT && values.some((v) => typeof v !== 'string')) {
    return values.length > 1 ? 'text values only' : 'a text value';
  }
  if (op === EConditionOp.BETWEEN && (values[0] as number) > (values[1] as number)) {
    return 'the first value must not be above the second';
  }
  const patterns = WILDCARD_OPS.includes(op) && kind === EConditionKind.TEXT ? values : [];
  if (
    patterns.some((v) => typeof v === 'string' && splitWildcard(v).length > MAX_WILDCARD_STARS + 1)
  ) {
    return `a pattern takes at most ${MAX_WILDCARD_STARS} "*"`;
  }
  return null;
}

export const FlowConditionGroupSchema = z.object({
  conditions: z.array(FlowConditionSchema).min(1).max(50),
});

/** Groups are OR-ed, conditions inside a group AND-ed (§5.2). */
export const FlowConditionFormSchema = z.object({
  groups: z.array(FlowConditionGroupSchema).min(1).max(20),
});

export type TFlowCondition = z.output<typeof FlowConditionSchema>;
export type TFlowConditionGroup = z.output<typeof FlowConditionGroupSchema>;
export type TFlowConditionForm = z.output<typeof FlowConditionFormSchema>;
